/**
 * Environment Configuration
 *
 * Centralized configuration for the Evidence Extractor application.
 * Reads environment variables and provides typed access to configuration values.
 */

/**
 * Application configuration interface
 */
export interface AppConfig {
  /** Base URL for the Management (Admin) API */
  managementApiUrl: string;
  /** Base URL for the Policy Navigator API */
  documentServiceApiUrl: string;
  /** Base URL for the Evidence Extractor API */
  evidenceExtractorApiUrl: string;
  /** Whether demo mode is enabled (uses mock data instead of live API) */
  demoMode: boolean;
  /** Current environment (dev/prod) */
  environment: string;
  /** Default schema ID for document processing */
  defaultSchemaId: string;
}

/**
 * Parse boolean environment variable
 */
const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined || value === '') return defaultValue;
  return value.toLowerCase() === 'true';
};

/**
 * Get environment variable with optional default
 */
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] ?? defaultValue;
};

/**
 * Application configuration loaded from environment variables
 *
 * Environment Variables:
 * - VITE_API_MANAGEMENT_URL: Management API base URL
 * - VITE_API_DOCUMENT_SERVICE_URL: Document Service API base URL
 * - VITE_API_EVIDENCE_EXTRACTOR_URL: Evidence Extractor API base URL
 * - VITE_DEMO_MODE: Enable demo mode with mock data (true/false)
 * - VITE_ENVIRONMENT: Current environment
 * - VITE_DEFAULT_SCHEMA_ID: Default schema ID for document processing
 *
 * Note: In development, leave VITE_API_URL empty to use Vite's proxy
 * which avoids CORS issues. The proxy is configured in vite.config.ts.
 */
export const config: AppConfig = {
  // Use empty string in dev to leverage Vite's proxy and avoid CORS
  managementApiUrl: getEnvVar('VITE_API_MANAGEMENT_URL', ''),
  documentServiceApiUrl: getEnvVar('VITE_API_DOCUMENT_SERVICE_URL', ''),
  evidenceExtractorApiUrl: getEnvVar('VITE_API_EVIDENCE_EXTRACTOR_URL', ''),
  demoMode: parseBoolean(getEnvVar('VITE_DEMO_MODE'), false),
  environment: getEnvVar('VITE_ENVIRONMENT', 'dev'),
  defaultSchemaId: getEnvVar('VITE_DEFAULT_SCHEMA_ID', ''),
};

/**
 * Check if the application is running in demo mode
 */
export const isDemoMode = (): boolean => config.demoMode;

/**
 * Check if the application is running in development environment
 */
export const isDevelopment = (): boolean => config.environment === 'dev';

/**
 * Check if the application is running in production environment
 */
export const isProduction = (): boolean => config.environment === 'prod';

/**
 * Validate required configuration for production mode
 * Returns an array of missing configuration keys
 */
export const validateConfig = (): string[] => {
  const missing: string[] = [];

  // API URL is required unless in demo mode
  if (!config.demoMode && !config.managementApiUrl) {
    missing.push('VITE_API_MANAGEMENT_URL');
  }

  return missing;
};

export default config;
